import { useState } from 'react';
import { Plus, Pencil, Ban, Search, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { useRotas } from '@/features/rotas/hooks/useRotas';
import type { Rota, SalvarRotaRequest } from '@/features/rotas/types';
import { usePaginacaoLocal } from '@/hooks/usePaginacaoLocal';
import { PaginacaoRodape } from '@/components/PaginacaoRodape';

const empty: SalvarRotaRequest = { descricao: '', referencia: '', isInativo: false };

const Rotas = () => {
  const [busca, setBusca] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmInativarId, setConfirmInativarId] = useState<number | null>(null);
  const [editingRota, setEditingRota] = useState<Rota | null>(null);
  const [form, setForm] = useState<SalvarRotaRequest>(empty);

  const { rotas, isLoading, criar, atualizar, inativar, isCriando, isAtualizando } = useRotas(busca || undefined);
  const paginacao = usePaginacaoLocal(rotas);

  const openNew = () => {
    setEditingRota(null);
    setForm(empty);
    setDialogOpen(true);
  };

  const openEdit = (rota: Rota) => {
    setEditingRota(rota);
    setForm({ descricao: rota.descricao, referencia: rota.referencia ?? '', isInativo: rota.isInativo });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!form.descricao.trim()) return;
    if (editingRota) {
      atualizar({ id: editingRota.id, req: form }, { onSuccess: () => setDialogOpen(false) });
    } else {
      criar(form, { onSuccess: () => setDialogOpen(false) });
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Rotas</h1>
          <p className="text-muted-foreground">Gerencie as rotas de entrega e visita</p>
        </div>
        <Button onClick={openNew}>
          <Plus className="h-4 w-4 mr-2" />Nova Rota
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar rota..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Descrição</TableHead>
              <TableHead>Referência</TableHead>
              <TableHead className="w-28">Status</TableHead>
              <TableHead className="w-24">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : paginacao.itensPaginados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                  Nenhuma rota encontrada.
                </TableCell>
              </TableRow>
            ) : paginacao.itensPaginados.map(rota => (
              <TableRow key={rota.id}>
                <TableCell className="font-medium">{rota.descricao}</TableCell>
                <TableCell className="text-muted-foreground">{rota.referencia || '—'}</TableCell>
                <TableCell>
                  <Badge variant={rota.isInativo ? 'secondary' : 'default'}>
                    {rota.isInativo ? 'Inativa' : 'Ativa'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" title="Editar" onClick={() => openEdit(rota)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    {!rota.isInativo && (
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Inativar"
                        onClick={() => setConfirmInativarId(rota.id)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <Ban className="h-4 w-4" />
                      </Button>
                    )}
                    {rota.isInativo && (
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Reativar"
                        onClick={() => atualizar({ id: rota.id, req: { ...form, isInativo: false, descricao: rota.descricao } })}
                        className="text-muted-foreground hover:text-green-600"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      {/* Dialog criar/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>{editingRota ? 'Editar Rota' : 'Nova Rota'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Descrição *</Label>
              <Input
                placeholder="Ex: Rota Centro"
                maxLength={60}
                value={form.descricao}
                onChange={e => setForm(f => ({ ...f, descricao: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Referência</Label>
              <Input
                placeholder="Referência opcional"
                maxLength={80}
                value={form.referencia ?? ''}
                onChange={e => setForm(f => ({ ...f, referencia: e.target.value }))}
              />
            </div>
            <div className="flex items-center gap-3 rounded-lg border px-3 py-2">
              <Switch
                checked={form.isInativo}
                onCheckedChange={v => setForm(f => ({ ...f, isInativo: v }))}
              />
              <Label className="font-normal">Inativa</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleSubmit}
              disabled={!form.descricao.trim() || isCriando || isAtualizando}
            >
              {isCriando || isAtualizando ? 'Salvando...' : editingRota ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmação inativar */}
      <AlertDialog open={!!confirmInativarId} onOpenChange={() => setConfirmInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar rota?</AlertDialogTitle>
            <AlertDialogDescription>
              A rota será marcada como inativa e não aparecerá nos combos do sistema.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (confirmInativarId) { inativar(confirmInativarId); setConfirmInativarId(null); } }}
              className="bg-destructive hover:bg-destructive/90"
            >
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Rotas;
